import api from './api';

export interface GDPRRequestData {
  user_email: string;
  full_name: string;
  request_type: string;
  description?: string;
}

export interface GDPRRequestResponse {
  success: boolean;
  message: string;
  request_id?: string;
}

export interface AuthenticatedGDPRRequestData {
  type: string;
  description?: string;
}

export interface GDPRRequest {
  id: string;
  request_type: string;
  status: string;
  description?: string;
  created_at: string;
  response_due_date: string;
  export_file_path?: string;
  export_expires_at?: string;
}

export interface UserDataSummary {
  user_info: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    created_at: string;
  };
  posts_count: number;
  media_files_count: number;
  ai_tasks_count: number;
  clients_count: number;
  total_storage_used: number;
}

const gdprService = {
  // Public GDPR request (no authentication required)
  async submitPublicGDPRRequest(requestData: GDPRRequestData): Promise<GDPRRequestResponse> {
    console.log('🚀 Starting GDPR request submission for:', requestData.user_email);
    
    // Try the main GDPR endpoint first (this is the one that works)
    try {
      console.log('🔍 Trying GDPR endpoint: /gdpr/request');
      
      const requestBody = {
        user_email: requestData.user_email,
        request_type: requestData.request_type,
        description: requestData.description
      };
      console.log('📋 Using GDPR format (backend schema):', requestBody);
      
      const response = await api.post<GDPRRequestResponse>('/gdpr/request', requestBody);
      console.log('✅ Success with GDPR endpoint:', response.data);
      
      // Transform the backend response to match our frontend interface
      return {
        success: true,
        message: 'GDPR request submitted successfully. We will verify your identity and respond within 30 days.',
        request_id: response.data.id
      };
    } catch (error: any) {
      console.log('❌ GDPR endpoint failed:', error.response?.status, error.response?.data);
      
      // If the main endpoint fails, try the contact form as fallback
      if (error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        console.log('🔄 Trying contact form fallback...');
        try {
          const fallbackBody = {
            name: requestData.full_name,
            email: requestData.user_email,
            subject: `GDPR ${requestData.request_type} Request`,
            message: `GDPR Request Type: ${requestData.request_type}\n\nDescription: ${requestData.description || 'No additional details provided'}`,
            inquiry_type: 'gdpr'
          };
          console.log('📧 Using contact form format:', fallbackBody);
          
          const fallbackResponse = await api.post('/contact/submit', fallbackBody);
          console.log('✅ Contact form fallback successful');
          
          return {
            success: true,
            message: 'Your GDPR request has been submitted via our contact form. We will respond within 30 days.',
            request_id: fallbackResponse.data.reference_id || 'contact-form-' + Date.now()
          };
        } catch (fallbackError) {
          console.error('❌ Contact form fallback also failed:', fallbackError);
          throw error; // Throw the original error
        }
      }
      
      throw error;
    }

  },

  // Authenticated GDPR request (requires authentication)
  async submitAuthenticatedGDPRRequest(requestData: AuthenticatedGDPRRequestData): Promise<GDPRRequestResponse> {
    const response = await api.post<GDPRRequestResponse>('/gdpr/request', {
      request_type: requestData.type,
      description: requestData.description
    });
    
    return response.data;
  },

  // Get user's GDPR requests
  async getMyRequests(): Promise<GDPRRequest[]> {
    const response = await api.get<GDPRRequest[]>('/gdpr/my-requests');
    return response.data;
  },

  // Get user's data summary
  async getMyDataSummary(): Promise<UserDataSummary> {
    const response = await api.get<UserDataSummary>('/gdpr/my-data');
    return response.data;
  },

  // Export user data
  async exportMyData(): Promise<{ success: boolean; message: string; export_id?: string }> {
    const response = await api.post('/gdpr/export-data');
    return response.data;
  },

  // Delete user account
  async deleteMyAccount(): Promise<{ success: boolean; message: string }> {
    const response = await api.delete('/gdpr/delete-account');
    return response.data;
  }
};

export default gdprService;